import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for MIDI lyrics/text events display
 * Syncs lyrics with MIDI playback timing
 */
export const useMidiLyrics = (midiData, currentTime) => {
  const [currentLyric, setCurrentLyric] = useState('');
  const [allLyrics, setAllLyrics] = useState([]);
  const lyricsTimelineRef = useRef([]);

  // Extract lyrics from MIDI data
  useEffect(() => {
    if (!midiData?.track) {
      setAllLyrics([]);
      lyricsTimelineRef.current = [];
      return;
    }

    // Look for lyrics in track meta events
    const track = midiData?.track;
    const lyrics = [];

    // Check if track has meta events with lyrics
    if (track?.meta && Array.isArray(track?.meta)) {
      track?.meta?.forEach(event => {
        if (event?.type === 'lyrics' || event?.type === 'text') {
          lyrics?.push({
            time: event?.time || 0,
            text: event?.text || ''
          });
        }
      });
    }

    // Sort by time
    lyrics?.sort((a, b) => a?.time - b?.time);
    
    setAllLyrics(lyrics);
    lyricsTimelineRef.current = lyrics;
  }, [midiData]);

  // Update current lyric based on playback time
  useEffect(() => {
    if (!lyricsTimelineRef?.current || lyricsTimelineRef?.current?.length === 0) {
      setCurrentLyric('');
      return;
    }

    // Find the most recent lyric before current time
    const currentLyricEvent = lyricsTimelineRef?.current?.filter(lyric => lyric?.time <= currentTime)?.pop();

    setCurrentLyric(currentLyricEvent?.text || '');
  }, [currentTime]);

  // Get lyric at specific time
  const getLyricAt = useCallback((time) => {
    if (!lyricsTimelineRef?.current || lyricsTimelineRef?.current?.length === 0) {
      return null;
    }

    const lyricEvent = lyricsTimelineRef?.current?.filter(lyric => lyric?.time <= time)?.pop();

    return lyricEvent || null;
  }, []);

  // Get upcoming lyrics (next N lyrics)
  const getUpcomingLyrics = useCallback((count = 3) => {
    if (!lyricsTimelineRef?.current || lyricsTimelineRef?.current?.length === 0) {
      return [];
    }

    return lyricsTimelineRef?.current?.filter(lyric => lyric?.time > currentTime)?.slice(0, count);
  }, [currentTime]);

  return {
    currentLyric,
    allLyrics,
    getLyricAt,
    getUpcomingLyrics,
    hasLyrics: allLyrics?.length > 0
  };
};